import { Environment } from "@workspace/access-button-launcher/dist/types";
import { dmnIdentity } from "./dmnIdentity";
import demo from "./environments/demo.json";
import dev from "./environments/dev.json";
import preProd from "./environments/preProd.json";
import production from "./environments/production.json";
import staging from "./environments/staging.json";
import { parseToken } from "./helpers";

export type GetEnvironmentPropsResponse = {
  ENVIRONMENT: string;
  SHOW_VERSION_NUMBER: boolean;
  APP_BASE_URL: string;
  BASE_DMN_URL: string;
  BASE_BOOKINGS_URL: string;
  NEW_CHECKOUT_URL: string;
  WORKSPACE_GRAPHQL_API: string;
  COLLINS_API_BASE_URL: string;
  CORE_API_BASE_URL: string;
  STRIPE_CONNECT_PUBLISHABLE_KEY?: string;
  AUTH_API_BASE_URL: string;
  WEB_SOCKET_URL: string | null;
  BROWSER_SIGN_IN_URL: string;
  BROWSER_SIGN_OUT_URL: string;
  IS_ACCESS_BUTTON_ENABLED: boolean;
  ACCESS_BUTTON_MENU_ITEMS: string;
  REACT_APP_CLARITY_TOKEN: string;
  ACCESS_BTN_ENV: Environment;
  INTERCOM_APP_ID: string;
  WOOTRIC_ACCOUNT_TOKEN: string | null;
  ACCESS_PRODUCT_CODE: string;
  CONFIGCAT_SDK_KEY: string | null;
  ADOPTION_TELEMETRY_URL?: string;
  FEATURE_SENTIMENT_API_HOST?: string;
  FEATURE_SENTIMENT_ACCOUNT_KEY?: string;
  FEATURE_SENTIMENT_REGISTRY_URL?: string;
};

export const isLocalDev = () => {
  return process.env.DEV === "TRUE";
};

/**
 * Picks the ConfigCat SDK key baked into the bundle at build time when one was
 * injected, else the fallback committed in the environment JSON. Azure leaves
 * an undefined pipeline variable as the literal `$(Variable_Name)` text, so a
 * value starting with `$` counts as unset — the same guard collins-mfe uses.
 */
export const resolveConfigCatSdkKey = (
  buildTimeKey: string | undefined,
  fallback: string | null
): string | null =>
  buildTimeKey && !buildTimeKey.startsWith("$") ? buildTimeKey : fallback;

// One bundle serves every environment, so both keys are baked in at build time
// and each environment's props resolve the key meant for it here, once, at
// module level — callers hold these objects in state/deps, so the references
// must stay stable.
const devProps: GetEnvironmentPropsResponse = {
  ...(dev as GetEnvironmentPropsResponse),
  CONFIGCAT_SDK_KEY: resolveConfigCatSdkKey(
    process.env.CONFIGCAT_SDK_KEY_PREPROD,
    dev.CONFIGCAT_SDK_KEY
  )
};

const preProdProps: GetEnvironmentPropsResponse = {
  ...(preProd as GetEnvironmentPropsResponse),
  CONFIGCAT_SDK_KEY: resolveConfigCatSdkKey(
    process.env.CONFIGCAT_SDK_KEY_PREPROD,
    preProd.CONFIGCAT_SDK_KEY
  )
};

const stagingProps = staging as GetEnvironmentPropsResponse;

const demoProps = demo as GetEnvironmentPropsResponse;

const productionProps: GetEnvironmentPropsResponse = {
  ...(production as GetEnvironmentPropsResponse),
  CONFIGCAT_SDK_KEY: resolveConfigCatSdkKey(
    process.env.CONFIGCAT_SDK_KEY_PROD,
    production.CONFIGCAT_SDK_KEY
  )
};

export const getEnvironmentProps = (
  token: string | undefined | null
): GetEnvironmentPropsResponse => {
  if (isLocalDev() && process.env.APP_ENV !== "preprod") {
    return devProps;
  }

  if (isLocalDev() && process.env.APP_ENV === "preprod" && !token) {
    return preProdProps;
  }

  const environment = isDmnURL() ? getEnvFromUrl() : getEnvFromToken(token);

  switch (environment?.toLowerCase()) {
    case "preprod":
      return preProdProps;
    case "staging":
      return stagingProps;
    case "demo":
      return demoProps;
    default:
      return productionProps;
  }
};

export const getEnvFromToken = (
  token: string | undefined | null
): string | null => {
  if (!token) return null;
  const decodedToken = parseToken(token);
  const issuer = decodedToken?.iss;
  if (!issuer) return null;
  const idx = issuer.indexOf("identity");
  if (idx === -1) return null;
  return issuer.substring(8, idx);
};

export const getEnvFromUrl = (href: string = location.href) => {
  return (
    href.split(".")?.[1]?.replace("dmn", "") ??
    href.split(".")?.[1]?.replace("designmynight", "")
  );
};

export const isBrowser = () => !window.ReactNativeWebView;

export const isNativeTab = () => window.location !== window.parent.location;

export const isDmnURL = (href: string = location.href) =>
  href.includes("dmn") || href.includes("designmynight");

export const getEnvironment = async (): Promise<
  GetEnvironmentPropsResponse | undefined
> => {
  const token = await dmnIdentity.getAccessToken();
  return getEnvironmentProps(token);
};

export const getBuildNumber = (): string => {
  const fullBuildNumber = process.env.BUILD_BUILDNUMBER ?? "Latest";
  const buildNumberSplit = fullBuildNumber.split(".");
  if (buildNumberSplit.length > 3) {
    return buildNumberSplit[buildNumberSplit.length - 1];
  }

  if (isLocalDev()) {
    return "Development";
  }

  return fullBuildNumber;
};

export const getVersionNumber = (): string => {
  const fullBuildNumber =
    process.env.BUILD_BUILDNUMBER ?? (isBrowser() ? "Web" : "Mobile");
  const buildNumberSplit = fullBuildNumber.split(".");
  if (buildNumberSplit.length > 3) {
    return buildNumberSplit.slice(0, -1).join(".");
  }
  return fullBuildNumber ?? "";
};
